/**
 * C-weighting curve per ANSI S1.43 / IEC 61672
 *
 * The C-weighting approximation uses the formula:
 *   R_C(f) = (12194^2 * f^2) / ((f^2 + 20.6^2) * (f^2 + 12194^2))
 *   A_C(f) = 20 * log10(|R_C(f)|) + 0.06
 *
 * At 1 kHz, A_C ~= 0 dB (by design).
 */

const F1 = 20.6;
const F4 = 12194.0;

const F1_SQ = F1 * F1;
const F4_SQ = F4 * F4;

/**
 * Compute the C-weighting relative response R_C(f) (linear, not in dB).
 */
export function cWeightingLinear(f: number): number {
  const fSq = f * f;
  const numerator = F4_SQ * fSq;
  const denominator = (fSq + F1_SQ) * (fSq + F4_SQ);
  if (denominator === 0) return 0;
  return numerator / denominator;
}

/**
 * Compute the C-weighting level correction A_C(f) in dB.
 * Returns the value in dB that should be added to a flat-spectrum
 * measurement to get C-weighted levels.
 *
 * Reference: IEC 61672-1:2013, Table 3
 */
export function cWeightingDb(f: number): number {
  const rc = cWeightingLinear(f);
  if (rc <= 0) return -Infinity;
  return 20 * Math.log10(rc) + 0.06;
}

/**
 * Compute the inverse C-weighting curve (the target for EQ correction).
 * This is -A_C(f): the shape the speaker must have so that a C-weighted
 * meter reads flat.
 *
 * Returns dB relative to 1 kHz reference.
 */
export function inverseCWeightingDb(f: number): number {
  return -cWeightingDb(f);
}

/**
 * Generate C-weighting dB values for an array of frequencies.
 */
export function cWeightingCurve(frequencies: number[]): Float64Array {
  const result = new Float64Array(frequencies.length);
  for (let i = 0; i < frequencies.length; i++) {
    result[i] = cWeightingDb(frequencies[i]);
  }
  return result;
}

/**
 * Generate inverse C-weighting (target) curve for an array of frequencies.
 */
export function inverseCWeightingCurve(frequencies: number[]): Float64Array {
  const result = new Float64Array(frequencies.length);
  for (let i = 0; i < frequencies.length; i++) {
    result[i] = inverseCWeightingDb(frequencies[i]);
  }
  return result;
}
